/**
 * SeasonContext — globally selected season (year) + session ("first" | "second"
 * | "rangers") for the entire app. Pages read the active session from here so
 * they all stay in lock-step with the sidebar.
 *
 * The session is persisted as a *label* ("first"/"second"/"rangers") rather
 * than a raw `termId`, because Term IDs change every year. On mount we resolve
 * the persisted label against the Term rows for the active year.
 *
 * Resolver rules (from CampHighlands.md §3):
 *   - "first":   Term where campMinderBunkPlanId IS NOT NULL and the name
 *                matches /1st\s*term/i (with a name-only fallback for non-CM
 *                tenants). 1st Term is the 3-week first half of the season.
 *   - "second":  same but matching /2nd\s*term/i.
 *   - "rangers": Term where campMinderBunkPlanId IS NULL (Rangers is a
 *                CampMinder Session, not a Bunk Plan) and the name matches
 *                /rangers/i.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { termsApi } from "../api/terms";
import { useAuth } from "../hooks/useAuth";
import type { Term } from "../types";

export type SessionLabel = "first" | "second" | "rangers";

const STORAGE_KEY_YEAR = "acts_selected_year";
const STORAGE_KEY_SESSION = "acts_selected_session";
/** Legacy key from the previous `selectedTermId`-based selector. Cleared on first run. */
const LEGACY_STORAGE_KEY_TERM = "acts_selected_term_id";

const SESSION_PRIORITY: readonly SessionLabel[] = ["first", "second", "rangers"];

const CURRENT_YEAR = new Date().getFullYear();

interface SeasonContextValue {
  /** All terms for this organisation, sorted by year desc then name */
  allTerms: Term[];
  /** Distinct years with terms, descending */
  availableYears: number[];
  /** Currently selected year */
  selectedYear: number;
  /** Terms that belong to the selected year */
  termsForYear: Term[];
  /** Currently selected session label (1st Term / 2nd Term / Rangers). */
  activeSession: SessionLabel;
  /** Sessions that resolve to a real Term in the selected year. */
  availableSessions: SessionLabel[];
  /** The Term resolved from `activeSession` for the selected year, if any. */
  selectedTerm: Term | null;
  /**
   * @deprecated Read `activeSession`/`selectedTerm` instead. Retained as a
   * derived value for pages that still consume term IDs directly. May be null
   * if the active session has no matching Term row in the selected year.
   */
  selectedTermId: number | null;
  setSelectedYear: (year: number) => void;
  setActiveSession: (s: SessionLabel) => void;
  /**
   * @deprecated Use `setActiveSession`. Provided for back-compat with older
   * call-sites that flipped the term selection by ID; it resolves the ID back
   * to a session label. `null` is treated as a request to fall back to the
   * default session for the current year.
   */
  setSelectedTermId: (id: number | null) => void;
  loading: boolean;
}

function isSessionLabel(value: string | null): value is SessionLabel {
  return value === "first" || value === "second" || value === "rangers";
}

/** Resolve a session label to the matching Term row in `terms` (1 year only). */
export function resolveSessionTerm(
  terms: readonly Term[],
  session: SessionLabel,
): Term | null {
  if (session === "rangers") {
    return (
      terms.find((t) => t.campMinderBunkPlanId == null && /rangers/i.test(t.name)) ??
      terms.find((t) => /rangers/i.test(t.name)) ??
      null
    );
  }
  const namePattern = session === "first" ? /1st\s*term/i : /2nd\s*term/i;
  return (
    terms.find(
      (t) =>
        t.campMinderBunkPlanId != null &&
        namePattern.test(t.name) &&
        !/rangers/i.test(t.name),
    ) ??
    terms.find((t) => namePattern.test(t.name) && !/rangers/i.test(t.name)) ??
    null
  );
}

/** Determine which session labels resolve to a real Term in the given year. */
export function listAvailableSessions(terms: readonly Term[]): SessionLabel[] {
  return SESSION_PRIORITY.filter((s) => resolveSessionTerm(terms, s) != null);
}

/** Reverse lookup: which session label does a given termId belong to? */
function sessionForTermId(terms: readonly Term[], termId: number): SessionLabel | null {
  const term = terms.find((t) => t.id === termId);
  if (!term) return null;
  if (/rangers/i.test(term.name)) return "rangers";
  if (/1st\s*term/i.test(term.name)) return "first";
  if (/2nd\s*term/i.test(term.name)) return "second";
  return null;
}

export const SeasonContext = createContext<SeasonContextValue>({
  allTerms: [],
  availableYears: [],
  selectedYear: CURRENT_YEAR,
  termsForYear: [],
  activeSession: "first",
  availableSessions: [],
  selectedTerm: null,
  selectedTermId: null,
  setSelectedYear: () => {},
  setActiveSession: () => {},
  setSelectedTermId: () => {},
  loading: false,
});

export function SeasonProvider({ children }: { children: React.ReactNode }) {
  const { organization } = useAuth();
  const orgSlug = organization?.slug;

  const [allTerms, setAllTerms] = useState<Term[]>([]);
  const [loading, setLoading] = useState(false);

  // Restore persisted selections from localStorage. The session label may
  // not yet resolve to a real Term — we'll reconcile that after terms load.
  const [selectedYear, setSelectedYearState] = useState<number>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_YEAR);
    return stored ? Number(stored) : CURRENT_YEAR;
  });
  const [activeSession, setActiveSessionState] = useState<SessionLabel>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_SESSION);
    return isSessionLabel(stored) ? stored : "first";
  });

  // One-shot cleanup of the legacy term-id key, since we now persist by label.
  useEffect(() => {
    if (localStorage.getItem(LEGACY_STORAGE_KEY_TERM) != null) {
      localStorage.removeItem(LEGACY_STORAGE_KEY_TERM);
    }
  }, []);

  const fetchTerms = useCallback(() => {
    if (!orgSlug) {
      setAllTerms([]);
      return;
    }
    setLoading(true);
    termsApi
      .list()
      .then((data) => {
        const sorted = [...data].sort(
          (a, b) => b.year - a.year || a.name.localeCompare(b.name),
        );
        setAllTerms(sorted);

        // Reconcile selectedYear against what's actually available.
        setSelectedYearState((prevYear) => {
          const years = [...new Set(sorted.map((t) => t.year))];
          if (!years.includes(prevYear)) {
            const defaultYear = years[0] ?? CURRENT_YEAR;
            localStorage.setItem(STORAGE_KEY_YEAR, String(defaultYear));
            return defaultYear;
          }
          return prevYear;
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgSlug]);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  const termsForYear = useMemo(
    () => allTerms.filter((t) => t.year === selectedYear),
    [allTerms, selectedYear],
  );

  const availableSessions = useMemo(
    () => listAvailableSessions(termsForYear),
    [termsForYear],
  );

  // If the persisted session doesn't exist for the active year, fall back to
  // the first available session. We only perform the fall-back after terms
  // have actually loaded (otherwise we'd flip away from a valid choice on
  // first render).
  useEffect(() => {
    if (loading || allTerms.length === 0) return;
    if (availableSessions.length === 0) return;
    if (!availableSessions.includes(activeSession)) {
      const fallback = availableSessions[0];
      setActiveSessionState(fallback);
      localStorage.setItem(STORAGE_KEY_SESSION, fallback);
    }
  }, [loading, allTerms.length, availableSessions, activeSession]);

  const selectedTerm = useMemo(
    () => resolveSessionTerm(termsForYear, activeSession),
    [termsForYear, activeSession],
  );

  const setSelectedYear = useCallback((year: number) => {
    setSelectedYearState(year);
    localStorage.setItem(STORAGE_KEY_YEAR, String(year));
    // Don't reset the session — we want "1st Term" to stay "1st Term" when
    // hopping between years. The reconciliation effect above will fall back
    // if the new year doesn't have the previously selected session.
  }, []);

  const setActiveSession = useCallback((s: SessionLabel) => {
    setActiveSessionState(s);
    localStorage.setItem(STORAGE_KEY_SESSION, s);
  }, []);

  const setSelectedTermId = useCallback(
    (id: number | null) => {
      if (id == null) {
        // Legacy "All Campers" path. Fall back to the first available
        // session so callers never observe a null active session.
        const fallback = availableSessions[0] ?? "first";
        setActiveSessionState(fallback);
        localStorage.setItem(STORAGE_KEY_SESSION, fallback);
        return;
      }
      const session = sessionForTermId(allTerms, id);
      if (session) {
        setActiveSessionState(session);
        localStorage.setItem(STORAGE_KEY_SESSION, session);
      }
    },
    [allTerms, availableSessions],
  );

  const availableYears = useMemo(
    () => [...new Set(allTerms.map((t) => t.year))].sort((a, b) => b - a),
    [allTerms],
  );

  const value: SeasonContextValue = {
    allTerms,
    availableYears,
    selectedYear,
    termsForYear,
    activeSession,
    availableSessions,
    selectedTerm,
    selectedTermId: selectedTerm?.id ?? null,
    setSelectedYear,
    setActiveSession,
    setSelectedTermId,
    loading,
  };

  return <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>;
}

export function useSeason() {
  return useContext(SeasonContext);
}

/** Display label for a session, used in headers and selectors. */
export const SESSION_DISPLAY_LABEL: Record<SessionLabel, string> = {
  first: "1st Term",
  second: "2nd Term",
  rangers: "Rangers",
};
